import { AlertTriangle, Info } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { formatAmount, formatPercent } from "@/lib/format"
import { cn } from "@/lib/utils"

import type { Ratio, RatiosRow } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

/**
 * Los ratios, con su base a la vista.
 *
 * UN PORCENTAJE SUELTO NO SE PUEDE AUDITAR: quien lo mira no sabe si el 40%
 * salio de doce meses o de los ocho que llevan datos. Cada tarjeta muestra el
 * numerador y el denominador debajo, en pequeno, para poder comprobarlo sin
 * salir de la pantalla.
 *
 * Y lo que NO se puede calcular se declara. Un panel de nueve ratios sugiere que
 * esos nueve son los que importan, y esconde que faltan los de capital de
 * trabajo. El backend manda esa lista con el motivo de cada ausencia.
 *
 * ---------------------------------------------------------------------------
 * AQUI NO HAY NINGUNA CIFRA EN ROJO, Y ES LA CORRECCION
 *
 * Estas doce tarjetas pintaban de rojo todo valor negativo. Con cuatro fuera de
 * rango salian tres o cuatro rojos en la misma pantalla —y encima de las siete
 * tarjetas de portada, que tambien pintaban los suyos—. La lamina 18 fija **un
 * solo dato en rojo por vista**, y el rojo que senala cuatro cosas no senala
 * ninguna: se vuelve el color de «este panel tiene numeros feos».
 *
 * Fuera de rango se dice con **el triangulo, el filete ocre del borde, el ocre
 * de la cifra y el recuento del encabezado**. Son cuatro señales, ninguna de
 * ellas solo color, que es justo lo que pide el brandbook: «los errores se
 * comunican con texto e icono, nunca solo con color».
 *
 * Y un negativo NO es una alerta. La conversion de EBITDA a caja negativa ya
 * viene marcada como fuera de rango por el backend, que es quien sabe el umbral;
 * un ratio negativo que el backend NO marco es un dato normal, y pintarlo de
 * rojo era la pantalla contradiciendo a quien calcula.
 */
export function RatiosPanel({
  datos, cargando, error, onRetry,
}: {
  datos?: RatiosRow
  cargando: boolean
  error: unknown
  onRetry: () => void
}) {
  const conAlerta = (datos?.ratios ?? []).filter((r) => r.alerta).length

  return (
    <WorkPanel
      title={t("Ratios")}
      meta={
        <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          {datos ? `${datos.periodo.meses} meses · ${datos.moneda}` : null}
          {conAlerta > 0 ? (
            <Badge variant="warning-soft">{conAlerta}{" "}{t("fuera de rango")}</Badge>
          ) : null}
        </span>
      }
    >
      <DataState
        loading={cargando}
        error={error}
        empty={Boolean(datos) && (datos?.ratios.length ?? 0) === 0}
        emptyMessage={t("No hay datos financieros para calcular ratios en este período.")}
        onRetry={onRetry}
        rows={3}
        columns={[25, 25, 25, 25]}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {(datos?.ratios ?? []).map((r) => <RatioCard key={r.id} ratio={r} />)}
        </div>

        {(datos?.no_disponibles ?? []).length > 0 ? (
          <div className="mt-3 space-y-1 border-t pt-2">
            <div className="eyebrow flex items-center gap-1">
              <Info className="h-3 w-3" />
              {t("Lo que todavía no se puede calcular")}</div>
            {datos?.no_disponibles.map((n) => (
              <p key={n.id} className="text-2xs leading-4 text-muted-foreground">
                <span className="font-medium text-foreground">{n.nombre}:</span> {n.motivo}
              </p>
            ))}
          </div>
        ) : null}
      </DataState>
    </WorkPanel>
  )
}

function valorLegible(r: Ratio): string {
  if (r.valor === null) return "—"
  if (r.formato === "pct") return formatPercent(r.valor, 1)
  if (r.formato === "veces") return `${r.valor.toFixed(2)}x`
  return formatAmount(r.valor)
}

function RatioCard({ ratio }: { ratio: Ratio }) {
  return (
    <div
      className={cn(
        "rounded-panel border bg-surface p-2.5",
        "transition-[box-shadow,border-color] duration-200 ease-out",
        "hover:shadow-[0_2px_10px_hsl(var(--foreground)/0.07)]",
        ratio.alerta && "border-warning/45",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="eyebrow">{ratio.nombre}</div>
        {ratio.alerta ? (
          <AlertTriangle className="mt-px h-3 w-3 shrink-0 text-warning" aria-hidden="true" />
        ) : null}
      </div>

      {/* `.cifra` = Light y tabular, como fija la lamina 18. El ocre marca lo
          que el BACKEND declaro fuera de rango; el signo menos, lo demas. */}
      <div
        className={cn(
          "cifra mt-1 text-xl leading-tight tracking-[-0.02em]",
          ratio.alerta && "text-warning",
        )}
      >
        {valorLegible(ratio)}
      </div>

      {/*
        La base. Sin esto el numero no se puede comprobar.

        `ratio.base` gana cuando viene: son los ratios que NO son un cociente y
        para los que «numerador / denominador» no significa nada. La Regla del
        40 mostraba «465.436,00 / 465.436,00» debajo de un −9,0%.
      */}
      <div className="num mt-0.5 text-2xs text-muted-foreground">
        {ratio.base
          ? ratio.base
          : ratio.formato === "moneda"
            ? `estructura ${formatAmount(ratio.numerador)}`
            : `${formatAmount(ratio.numerador)} / ${formatAmount(ratio.denominador)}`}
      </div>

      <p className="mt-1 text-2xs leading-4 text-muted-foreground">{ratio.interpretacion}</p>
    </div>
  )
}
